/**
 * Analytics utility for SPA - Umami
 * Self-hosted, GDPR-compliant, cookie-free
 * 
 * Tracks with human-readable names instead of raw URLs:
 * - Page views: Uses page name/description
 * - Image views: Uses image description with parent page context
 * - Interactions: Contact clicks, external links, interesting content
 * 
 * NOTE: Never tracked:
 *  - Admin SPA (/admin/*)
 *  - Appearance preview iframe (?appearancePreview=1 / data-appearance-preview)
 *  - Any iframe whose same-origin parent is /admin
 *  - Browsers with window.__ADMIN__ (logged-in admin on the public site)
 * Logging in to admin also opts the browser out permanently: SKIP_ANALYTICS_KEY
 * cookie (server skips embedding Umami) and Umami's `umami.disabled` localStorage
 * flag (no collection even if the script loads).
 */

export const SKIP_ANALYTICS_KEY = 'skip_analytics';

// Umami's native opt-out: when this localStorage key is truthy, the Umami
// script disables ALL tracking (including its automatic pageview) on any page
// it loads on. localStorage persists indefinitely, so once a user has logged
// into the admin they are never tracked again - even if cookies are cleared.
export const UMAMI_DISABLED_KEY = 'umami.disabled';

/** Matches lib/theme/tokens.js — duplicated here to keep this module dependency-free. */
const APPEARANCE_PREVIEW_QUERY = 'appearancePreview';

// Effectively-permanent cookie lifetime (10 years) so the server keeps skipping
// the Umami script embed for anyone who has ever logged into the admin.
const SKIP_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10;

/**
 * Check if current path is an admin route
 */
function isAdminRoute() {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/admin');
}

/** Appearance studio preview frame (query or latched data attribute). */
function isAppearancePreviewFrame() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    if (document.documentElement?.dataset?.appearancePreview === '1') return true;
    try {
        return new URLSearchParams(window.location.search).get(APPEARANCE_PREVIEW_QUERY) === '1';
    } catch {
        return false;
    }
}

/** Preview iframe under the admin shell (same-origin parent at /admin). */
function isAdminEmbeddedFrame() {
    if (typeof window === 'undefined' || window.self === window.top) return false;
    try {
        return window.parent?.location?.pathname?.startsWith('/admin') === true;
    } catch {
        return false;
    }
}

/**
 * Read skip_analytics cookie (used when script was already loaded before cookie was set)
 */
function getSkipAnalyticsCookie() {
    if (typeof document === 'undefined') return null;
    const m = document.cookie.match(new RegExp(`(?:^|; )${SKIP_ANALYTICS_KEY}=([^;]*)`));
    return m ? m[1] : null;
}

/**
 * Read Umami's native opt-out flag from localStorage.
 */
function hasUmamiDisabledFlag() {
    if (typeof window === 'undefined') return false;
    try {
        return !!window.localStorage?.getItem(UMAMI_DISABLED_KEY);
    } catch {
        return false;
    }
}

/**
 * Persist the analytics opt-out for the current browser.
 *
 * Called when a user logs into the admin. Sets both:
 *  - the `skip_analytics` cookie, so the server never embeds the Umami script, and
 *  - Umami's native `umami.disabled` localStorage flag, so that even if the
 *    script ever does load it collects nothing (auto pageview included).
 */
export function markAnalyticsOptOut() {
    if (typeof window === 'undefined') return;
    try {
        document.cookie = `${SKIP_ANALYTICS_KEY}=1; path=/; max-age=${SKIP_COOKIE_MAX_AGE}; samesite=lax`;
    } catch {
        // ignore cookie errors (private mode, etc.)
    }
    try {
        window.localStorage?.setItem(UMAMI_DISABLED_KEY, '1');
    } catch {
        // ignore storage errors (private mode, etc.)
    }
}

/**
 * Re-assert the opt-out cookie from the persistent localStorage flag.
 *
 * Run on the public site so that a user who has ever logged in (localStorage
 * flag present) but whose cookie was cleared gets the cookie restored - which
 * stops the server from embedding the Umami script on subsequent loads.
 */
export function ensureAnalyticsOptOutPersisted() {
    if (typeof window === 'undefined') return;
    if (!hasUmamiDisabledFlag()) return;
    if (getSkipAnalyticsCookie() === '1') return;
    try {
        document.cookie = `${SKIP_ANALYTICS_KEY}=1; path=/; max-age=${SKIP_COOKIE_MAX_AGE}; samesite=lax`;
    } catch {
        // ignore cookie errors
    }
}

/**
 * Check if analytics should be skipped (admin, preview iframe, opt-out).
 */
function shouldSkipAnalytics() {
    if (typeof window === 'undefined') return true;
    if (isAdminRoute()) return true;
    if (isAppearancePreviewFrame()) return true;
    if (isAdminEmbeddedFrame()) return true;
    if (window.__ADMIN__ === true) return true;
    if (getSkipAnalyticsCookie() === '1') return true;
    return hasUmamiDisabledFlag();
}

/**
 * Disable Umami immediately in the Appearance preview iframe so an auto
 * pageview cannot fire even if the script tag was already in the HTML.
 */
export function disableUmamiForPreview() {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage?.setItem(UMAMI_DISABLED_KEY, '1');
    } catch {
        // ignore
    }
    try {
        if (window.umami && typeof window.umami === 'object') {
            window.umami = { ...window.umami, track: () => undefined };
        }
    } catch {
        // ignore
    }
}

/**
 * Safely call umami.track
 * @param {string} eventName - Event name (optional, omit for pageview)
 * @param {Object} eventData - Event data (optional)
 */
function track(eventName, eventData) {
    if (typeof window === 'undefined') return;
    if (shouldSkipAnalytics()) return;
    if (typeof window.umami?.track !== 'function') return;
    
    if (eventName) {
        window.umami.track(eventName, eventData);
    } else {
        window.umami.track();
    }
}

/**
 * Track page view with human-readable name
 * Sends both the page name and URL for context
 * 
 * @param {Object} options
 * @param {string} options.pageName - Human-readable page name
 * @param {string} [options.pageDescription] - Optional page description
 * @param {string} [options.parentPage] - Parent page name (for nested pages)
 * @param {string} [options.pageType] - Page type (masonry, slideshow, html)
 */
export function trackPageView({ pageName, pageDescription, parentPage, pageType } = {}) {
    if (typeof window === 'undefined') return;
    if (shouldSkipAnalytics()) return;
    
    // If no page info provided, just trigger default Umami pageview
    if (!pageName) {
        track();
        return;
    }
    
    // Build display name: "Parent > Page" or just "Page"
    const displayName = parentPage ? `${parentPage} > ${pageName}` : pageName;
    
    // Track as custom event with page context
    track('page_view', {
        page: displayName,
        description: pageDescription || pageName,
        type: pageType || 'unknown',
        url: window.location.pathname,
    });
}

/**
 * Track when a project/gallery page is viewed
 * @param {Object} options
 * @param {string} options.name - Project/page name
 * @param {string} [options.description] - Page description
 * @param {string} [options.parentName] - Parent page name
 * @param {string} [options.galleryType] - Gallery type (masonry, slideshow)
 * @param {number} [options.imageCount] - Number of images in gallery
 */
export function trackProjectViewed({ name, description, parentName, galleryType, imageCount }) {
    if (!name) return;
    
    const displayName = parentName ? `${parentName} > ${name}` : name;
    
    track('project_viewed', {
        project: displayName,
        description: description || name,
        type: galleryType || 'gallery',
        images: imageCount || 0,
    });
}

/**
 * Track when a gallery media item is opened in the viewer
 * @param {Object} options
 * @param {string} options.imageName - File ID
 * @param {string} [options.imageDescription] - Human-readable media description
 * @param {string} [options.originalName] - Original filename of the media
 * @param {string} options.pageName - Parent page name (use "Home" for home page)
 * @param {string} [options.pageDescription] - Parent page description
 * @param {string} [options.grandparentName] - Grandparent page (for nested pages)
 * @param {number} [options.imageIndex] - Image position in gallery (1-based)
 * @param {number} [options.totalImages] - Total images in gallery
 * @param {'image'|'video'} [options.mediaType] - Opened media type
 */
export function trackMediaOpened({ 
    imageName, 
    imageDescription, 
    originalName,
    pageName, 
    pageDescription,
    grandparentName,
    imageIndex,
    totalImages,
    mediaType = 'image',
}) {
    // Default to "Home" if no page name (home page images)
    const page = pageName || 'Home';
    
    // Build page context: "Grandparent > Parent" or just "Parent"
    const pageContext = grandparentName ? `${grandparentName} > ${page}` : page;
    
    // Use description if available, otherwise fall back to original filename, then ID
    const displayImage = imageDescription || originalName || imageName || 'Unknown image';
    
    // Build position string if available
    const position = imageIndex && totalImages ? `${imageIndex}/${totalImages}` : null;
    
    // Format: "{media} on {page}" for cleaner Umami logs
    const eventName = `${displayImage} on ${pageContext}`;
    const type = mediaType === 'video' ? 'video' : 'image';
    const eventKey = type === 'video' ? 'video_viewed' : 'image_viewed';
    
    track(eventKey, {
        name: eventName,
        // Stable media id for reliable enrichment in admin analytics
        // (joins back to files[id] regardless of description changes).
        imageId: imageName || null,
        image: displayImage,
        mediaType: type,
        page: pageContext,
        position,
    });
}

/**
 * Backward-compatible alias used throughout existing image viewers.
 */
export function trackImageOpened(options = {}) {
    trackMediaOpened({ ...options, mediaType: 'image' });
}

/**
 * Track contact/interaction clicks
 * @param {string} method - Contact method (email, instagram, phone, etc.)
 * @param {string} [label] - Optional label for the link
 */
export function trackContactClick(method, label) {
    track('contact_click', { 
        method,
        label: label || method,
    });
}

/**
 * Track when user interacts with interesting content
 * Use this for any notable user interactions
 * 
 * @param {Object} options
 * @param {string} options.action - Action type (click, scroll, hover, etc.)
 * @param {string} options.element - What was interacted with
 * @param {string} [options.context] - Page or section context
 * @param {Object} [options.metadata] - Additional data
 */
export function trackInteraction({ action, element, context, metadata }) {
    if (!action || !element) return;
    
    track('interaction', {
        action,
        element,
        context: context || window?.location?.pathname,
        ...metadata,
    });
}

/**
 * Track external link clicks
 * @param {string} url - The external URL
 * @param {string} [label] - Human-readable label for the link
 * @param {string} [context] - Where the link was clicked from
 */
export function trackExternalLink(url, label, context) {
    if (!url) return;
    
    // Extract domain for cleaner tracking
    let domain = '';
    try {
        domain = new URL(url).hostname;
    } catch {
        domain = url;
    }
    
    track('external_link', {
        url,
        domain,
        label: label || domain,
        context: context || window?.location?.pathname,
    });
}

/**
 * Track gallery navigation (prev/next)
 * @param {Object} options
 * @param {string} options.direction - 'next' or 'prev'
 * @param {string} options.pageName - Gallery/page name
 * @param {number} options.fromIndex - Previous image index
 * @param {number} options.toIndex - New image index
 * @param {string} options.method - Navigation method (click, swipe, keyboard)
 */
export function trackGalleryNavigation({ direction, pageName, fromIndex, toIndex, method }) {
    track('gallery_navigation', {
        direction,
        page: pageName,
        from: fromIndex,
        to: toIndex,
        method: method || 'unknown',
    });
}

/**
 * Track scroll depth on a page
 * Call this when user scrolls past certain thresholds (25%, 50%, 75%, 100%)
 * @param {Object} options
 * @param {string} options.pageName - Page name
 * @param {number} options.depth - Scroll depth percentage (25, 50, 75, 100)
 */
export function trackScrollDepth({ pageName, depth }) {
    if (!pageName || !depth) return;
    
    track('scroll_depth', {
        page: pageName,
        depth: `${depth}%`,
    });
}

// Legacy exports for backward compatibility
export { trackPageView as trackPageViewLegacy };
